import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import {
  AVG_GAMEPLAYS_THRESHOLD,
  MAX_PREDICTOR_GAMEPLAYS,
  MIN_USERS_FOR_PREDICTION,
  PREDICTOR_CACHE_EXPIRATION_TIMEOUT,
} from './constants';
import { ISuccessPredictorDataEntry, IWorkoutSuccessPredictor, IWorkoutSuccessPredictorCache } from './types';

export const findWorkoutPrediction = (predictions: IWorkoutSuccessPredictor[], workoutId: number) =>
  find(predictions, { workoutId });

export const isEnoughUsers = (dataEntry: ISuccessPredictorDataEntry) =>
  dataEntry.numUsersForLevelUp >= MIN_USERS_FOR_PREDICTION;

export const sortByStartDifficulty = (data: ISuccessPredictorDataEntry[]) => sortBy(data, ['startDifficulty']);

/**
 * checks if cache has expired
 * @param syncTimestamp synchronization timestamp for the given cache value
 */
const isCacheExpired = (syncTimestamp: number) => Date.now() - syncTimestamp >= PREDICTOR_CACHE_EXPIRATION_TIMEOUT;

/**
 * checks if cache value is invalid (not set or expired)
 * @param value cache value
 */
export const isCacheInvalid = (value: undefined | IWorkoutSuccessPredictorCache) => {
  if (!value) {
    return true;
  }
  return isCacheExpired(value.syncTimestamp);
};

export function calculateAvgGameplaysForLevelUp(difficulty: number, prediction?: IWorkoutSuccessPredictor) {
  if (!prediction) {
    // no prediction found - either not loaded or not generated on backend
    // we return prediction bigger than max to hide the widget
    return MAX_PREDICTOR_GAMEPLAYS + 1;
  }
  // sort data in case they are received unsorted
  const dataEntries = sortByStartDifficulty(prediction.data);
  // find entry for given difficulty fullfilling the users requirement
  const foundPrediction = find(dataEntries, { startDifficulty: difficulty });
  if (!foundPrediction || !isEnoughUsers(foundPrediction)) {
    // if exact prediction not found or not enough user counted - hide the widget
    return MAX_PREDICTOR_GAMEPLAYS + 1;
  }
  // if prediction was not found because of low amount of users - return 0
  return foundPrediction ? Math.ceil(foundPrediction.avgGameplaysForLevelUp) : 0;
}

export function isInValidRange(value: number) {
  return value >= 0 && value <= MAX_PREDICTOR_GAMEPLAYS;
}

export function normalizeExpectedRemainingGameplays(avgGameplaysForLevelUpAllUsers: number, completionCount: number) {
  // apply threshold only for values that are already in valid range
  const value = isInValidRange(avgGameplaysForLevelUpAllUsers)
    ? avgGameplaysForLevelUpAllUsers + AVG_GAMEPLAYS_THRESHOLD - completionCount
    : MAX_PREDICTOR_GAMEPLAYS + 1;
  return value;
}
